//@ts-check
import adaptiveApi from 'api/adaptiveAiApi';
import useFetch from 'hooks/use-fetch';
import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-form';
import { Form } from 'reactstrap';
import classNames from 'classnames';
import { CheckCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Col, Row, Skeleton } from 'antd';
import ThemeButton from 'components/AntdFormInputs/theme-button';
import { QUESTION_TYPE, UNIT_STATUS } from 'constants/constants';
import { handleToastError, stripHtml } from 'helpers/index';
import { completeUnitAsync, fetchUnitAsync } from 'store/units';
import { getSelectedCentre } from 'store/master';
import { showToastSuccess } from '@core/components/toastify/ToastTypes';
import { getThemeColor } from 'store/settings';
import {
  DragAndDrop,
  FillInTheBlank,
  FillTheGap,
  FreeText,
  MultipleChoice,
  Ordering,
} from 'modules/Course/Unit/components/QuestionTypev2';
import styles from 'styles/Unit.module.scss';

/** @param {string} hex */
function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (h.length !== 6 || Number.isNaN(parseInt(h, 16))) return null;
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** @param {string} hex @param {number} [alpha] */
function rgbaFromHex(hex, alpha = 1) {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(99, 102, 241, ${alpha})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

/** @param {string} hex @param {number} [amount] */
function mixWithWhite(hex, amount = 0.35) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex || '#6366f1';
  const r = Math.round(rgb.r + (255 - rgb.r) * amount);
  const g = Math.round(rgb.g + (255 - rgb.g) * amount);
  const b = Math.round(rgb.b + (255 - rgb.b) * amount);
  return `rgb(${r},${g},${b})`;
}

/**
 * CSS variables for micro-learning panel (theme from Redux `getThemeColor`).
 * @param {string} [themeColor]
 */
function getMicroLearningThemeVars(themeColor) {
  const primary =
    themeColor && typeof themeColor === 'string' && themeColor.trim() !== ''
      ? themeColor.trim()
      : '#6366f1';
  return {
    '--ml-primary': primary,
    '--ml-gradient-mid': mixWithWhite(primary, 0.38),
    '--ml-gradient-accent': mixWithWhite(primary, 0.12),
    '--ml-badge-end': mixWithWhite(primary, 0.22),
    '--ml-icon-glow': rgbaFromHex(primary, 0.22),
    '--ml-glow-a': rgbaFromHex(primary, 0.12),
    '--ml-glow-b': rgbaFromHex(primary, 0.06),
    '--ml-glow-c': rgbaFromHex(primary, 0.14),
  };
}

/** Show API micro-learning payload as plain text (strips tags if any). */
function microLearningContentAsText(raw) {
  if (raw == null) return '';
  const s = typeof raw === 'string' ? raw : String(raw);
  return stripHtml(s).trim();
}

const EMPTY_FORM_DEFAULTS = {
  multipleChoice: [],
  fillInTheBlank: [],
  fillTheGap: [],
  ordering: [],
  freeText: '',
  dragDrop: [],
};

function getFormDataForQuestion(questionType, value) {
  const hasResultValue = value?.result?.length;
  let formValue;
  if (hasResultValue) {
    formValue = value?.result;
  } else {
    formValue = value?.data;
  }
  switch (questionType) {
    case QUESTION_TYPE.MTC:
      return {
        value: value?.result,
        label: 'multipleChoice',
      };
    case QUESTION_TYPE.FILLINTHEBLANK:
      return {
        value: value?.result,
        label: 'fillInTheBlank',
      };
    case QUESTION_TYPE.FILLTHEGAP:
      return {
        value: value?.result,
        label: 'fillTheGap',
      };
    case QUESTION_TYPE.ORDERING:
      return {
        value: formValue?.map((item) => ({
          id: crypto.randomUUID(),
          content: item,
        })),
        label: 'ordering',
      };
    case QUESTION_TYPE.DRAGDROP:
      return {
        value: formValue?.map((item) => ({
          id: crypto.randomUUID(),
          content: item?.correct || item,
        })),
        label: 'dragDrop',
      };
    case QUESTION_TYPE.FREETEXT:
      return {
        value: value?.dataContent ?? '',
        label: 'freeText',
      };
    default:
      return {
        value: [],
        label: '',
      };
  }
}

function buildAnswerPayload(formData, questionType) {
  switch (questionType) {
    case QUESTION_TYPE.MTC:
      return formData.multipleChoice;
    case QUESTION_TYPE.FILLINTHEBLANK:
      return formData.fillInTheBlank;
    case QUESTION_TYPE.FILLTHEGAP:
      return formData.fillTheGap;
    case QUESTION_TYPE.ORDERING:
      return formData.ordering?.map((item) => item.content);
    case QUESTION_TYPE.DRAGDROP:
      return formData.dragDrop?.map((item) => item.content);
    case QUESTION_TYPE.FREETEXT:
      return formData.freeText ?? '';
    default:
      return null;
  }
}

function extractMicroLearningHtml(result) {
  if (result == null) return null;
  if (typeof result === 'string') {
    return result.trim() || null;
  }
  const raw =
    result.microLearningContent ??
    result.micro_learning_content ??
    result?.data?.microLearningContent;
  if (raw == null) return null;
  if (typeof raw === 'string') return raw.trim() || null;
  if (typeof raw === 'object') {
    if (typeof raw.html === 'string' && raw.html.trim()) return raw.html;
    if (typeof raw.content === 'string' && raw.content.trim())
      return raw.content;
  }
  return null;
}

function isTerminalAttemptStatus(status) {
  if (status == null) return false;
  return status === UNIT_STATUS.FAILED || status === UNIT_STATUS.COMPLETED;
}

function normalizeAiMicroLearningHtml(raw) {
  return extractMicroLearningHtml(raw);
}

function findMicroLearningRowForTerminalStatus(list) {
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.find((row) => row?.userQuestion?.aiMicroLearningContent);
}

function listHasAnyAiMicroLearning(list) {
  if (!Array.isArray(list)) return false;
  return list.some((row) => row?.userQuestion?.aiMicroLearningContent);
}

function resolveOutcomeFromAdaptiveQuestionList(list, unitStatus) {
  const row = findMicroLearningRowForTerminalStatus(list);
  if (row) {
    const html = normalizeAiMicroLearningHtml(
      row.userQuestion.aiMicroLearningContent,
    );
    if (html) {
      return { kind: 'micro', html };
    }
  }

  if (
    unitStatus === UNIT_STATUS.COMPLETED ||
    unitStatus === UNIT_STATUS.FAILED
  ) {
    return { kind: 'complete' };
  }

  return null;
}

function reFactorQuestion(list, activeQuestionId) {
  const hasLength = !!list.length;
  let row = null;
  if (activeQuestionId) {
    row =
      list.find((item) => item.question?._id === activeQuestionId) ?? list[0];
  } else if (hasLength) {
    row = list[0];
  }
  return {
    ...row?.userQuestion,
    questionNumber: row?.question?.questionNumber ?? 1,
    questionId: row?.question?._id ?? null,
  };
}

function resolveOutcomeFromSubmitResponse(result) {
  const html = extractMicroLearningHtml(result);
  if (html) return { kind: 'micro', html };
  return { kind: 'complete' };
}

function QuestionAnswerSkeleton() {
  return (
    <div className="question-answer-skeleton -mt-2">
      <Skeleton
        active
        paragraph={{ rows: 2, width: ['100%', '75%'] }}
        title={{ width: '40%', style: { marginTop: 2 } }}
        className="mb-3"
      />
    </div>
  );
}

function QuestionList({
  unitId,
  totalQuestions,
  unitStatus = undefined,
  microLearningContent,
}) {
  const dispatch = useDispatch();
  const themeColor = useSelector(getThemeColor);
  const [currentQuestion, setCurrentQuestion] = useState(null);

  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [submitOutcome, setSubmitOutcome] = useState(null);
  const [isRestarting, setIsRestarting] = useState(false);

  const questionType = currentQuestion?.type;
  const questionNumber = currentQuestion?.questionNumber;

  const microLearningThemeVars = useMemo(
    () => getMicroLearningThemeVars(themeColor),
    [themeColor],
  );

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    defaultValues: EMPTY_FORM_DEFAULTS,
    shouldUnregister: false,
  });

  const { loading: isInitialLoading, error: fetchError } = useFetch({
    queryKey: [
      'adaptive-ai-unit-question',
      unitId,
      currentQuestionId,
      unitStatus,
    ],
    queryFn: () => adaptiveApi.getAllAiQuestion(unitId),
    enable: !!unitId,
    onSuccess: (response) => {
      const list = Array.isArray(response) ? response : [response];

      const question = reFactorQuestion(list, currentQuestionId);

      if (list.length > 0) {
        setCurrentQuestion(question);
        if (question?.questionId) {
          localStorage.setItem('currentQuestionId', question.questionId);
        }
      } else {
        setCurrentQuestion(null);
      }
    },
  });

  useEffect(() => {
    if (fetchError) handleToastError(fetchError);
  }, [fetchError]);

  useEffect(() => {
    if (currentQuestion) {
      const { label, value } = getFormDataForQuestion(
        questionType,
        currentQuestion,
      );
      setValue(label, value);
    }
  }, [currentQuestion]);

  const cssClass = useMemo(() => {
    const t = questionType;
    return classNames('d-flex fill-overflow-image w-100', {
      fillinthegapstyle: t === QUESTION_TYPE.FILLTHEGAP,
      'align-items-baseline': t === QUESTION_TYPE.FILLINTHEBLANK,
    });
  }, [questionType]);

  const onSubmit = async (formData, navigation = 'next') => {
    const payload = buildAnswerPayload(formData, questionType);

    try {
      const body = {
        data: payload,
        id: currentQuestion?._id,
        isEmpty: false,
      };

      const typesUsingArrayPayload = [
        QUESTION_TYPE.FILLINTHEBLANK,
        QUESTION_TYPE.FILLTHEGAP,
        QUESTION_TYPE.ORDERING,
        QUESTION_TYPE.DRAGDROP,
        QUESTION_TYPE.MTC,
      ];
      if (
        typesUsingArrayPayload.includes(questionType) &&
        Array.isArray(payload) &&
        payload.length === 0
      ) {
        body.isEmpty = true;
      } else if (questionType === QUESTION_TYPE.FREETEXT && !payload) {
        body.isEmpty = true;
      }

      if (navigation === 'submit') {
        const result = await adaptiveApi.getSubmit(
          unitId,
          currentQuestion?.questionId,
          body,
        );
        if (result) {
          await fetchUnitAsync(unitId)(dispatch);
          showToastSuccess('Adaptive test submitted successfully');
        }
      } else if (navigation === 'next' || navigation === 'prev') {
        const nextIndex =
          navigation === 'next'
            ? Math.min(currentQuestion?.questionNumber + 1, totalQuestions)
            : Math.max(currentQuestion?.questionNumber - 1, 1);

        const updateResult = await adaptiveApi.updateApi(body, {
          unitId,
          questionId: currentQuestion?.questionId,
          questionIndex: currentQuestion?.questionNumber,
          nextIndex,
        });
        if (updateResult?.question?._id) {
          setCurrentQuestionId(updateResult?.question?._id);
          reset({});
        }
      }
    } catch (error) {
      handleToastError(error);
    }
  };

  const goPrev = () => {
    handleSubmit((formData) => onSubmit(formData, 'prev'))();
  };

  const goNext = () => {
    handleSubmit((formData) => onSubmit(formData, 'next'))();
  };

  const handleRestartAdaptiveTest = async () => {
    if (!unitId) return;
    try {
      setIsRestarting(true);

      const response = await adaptiveApi.startAdaptiveAiUnitQuestion(unitId);
      await fetchUnitAsync(unitId)(dispatch);
      if (response?._id) {
        showToastSuccess('Adaptive learning started successfully');
      }
      localStorage.removeItem('currentQuestionId');
      setCurrentQuestionId(null);
      setSubmitOutcome(null);
      reset(EMPTY_FORM_DEFAULTS);
    } catch (error) {
      handleToastError(error);
    } finally {
      setIsRestarting(false);
    }
  };

  if (!unitId) {
    return <p className="text-muted small mb-0">No unit selected.</p>;
  }

  if (microLearningContent && unitStatus === UNIT_STATUS.FAILED) {
    return (
      <div
        className={classNames(
          'adaptive-submit-outcome px-1 w-100',
          styles.microLearningSection,
          styles.microLearningThemeRoot,
        )}
        style={
          /** @type {import('react').CSSProperties} */
          ({ ...microLearningThemeVars })
        }
      >
        <div className={styles.microLearningHeader}>
          <ThunderboltOutlined
            className={styles.microLearningIcon}
            aria-hidden
          />
          <span className={styles.microLearningTitle}>Micro learning</span>
          <span className={styles.microLearningBadge}>AI</span>
        </div>
        <div className={styles.microLearningGlow}>
          <div className={styles.microLearningInner}>
            <p className={`mb-0 ${styles.microLearningPlainText}`}>
              {microLearningContentAsText(microLearningContent)}
            </p>
          </div>
        </div>
        <div className="d-flex justify-content-center mt-4">
          <ThemeButton
            type="button"
            padding="7px 18px"
            className="rounded-pill"
            onClick={handleRestartAdaptiveTest}
            isLoading={isRestarting}
          >
            Start
          </ThemeButton>
        </div>
      </div>
    );
  }

  if (submitOutcome?.kind === 'complete') {
    return (
      <div className="adaptive-submit-outcome d-flex flex-column align-items-center justify-content-center py-5 text-center px-3 w-100">
        <span className="h5 mb-1 font-weight-semibold ">Complete</span>
        <span className="text-muted small">
          You have completed this adaptive test.
        </span>
      </div>
    );
  }

  if (isInitialLoading) {
    return <QuestionAnswerSkeleton />;
  }

  if (!currentQuestion) {
    return (
      <p className="text-muted small mb-0">
        No question loaded. Try refreshing the page.
      </p>
    );
  }

  return (
    <Form
      onSubmit={(e) => e.preventDefault()}
      className="require-label adaptive-question-list"
    >
      <Row className={cssClass} wrap>
        <Col span={24}>
          <p className="mb-3 text-sm text-gray-700 pt-2 d-flex">
            <span className="mr-2 question-text">{`${questionNumber}.`}</span>
            {questionType !== QUESTION_TYPE.FILLINTHEBLANK &&
              questionType !== QUESTION_TYPE.FILLTHEGAP && (
                <span
                  dangerouslySetInnerHTML={{
                    __html: currentQuestion?.content,
                  }}
                />
              )}
            {questionType === QUESTION_TYPE.FILLINTHEBLANK && (
              <Controller
                name="fillInTheBlank"
                control={control}
                render={({ field }) => (
                  <FillInTheBlank
                    contentHTML={
                      currentQuestion?.contentHTML ||
                      currentQuestion?.modifiedContent
                    }
                    className="ml-1 text-word-break fill-blank w-100 fill-overflow-image d-flex flex-wrap"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
            {questionType === QUESTION_TYPE.FILLTHEGAP && (
              <Controller
                name="fillTheGap"
                control={control}
                render={({ field }) => (
                  <FillTheGap
                    contentHTML={
                      currentQuestion?.contentHTML ||
                      currentQuestion?.modifiedContent
                    }
                    className="ml-1 text-word-break fill-blank w-100 fill-overflow-image d-flex flex-wrap"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </p>

          {questionType === QUESTION_TYPE.ORDERING && (
            <Controller
              name="ordering"
              control={control}
              render={({ field }) => (
                <Ordering value={field.value} onChange={field.onChange} />
              )}
            />
          )}

          {questionType === QUESTION_TYPE.FREETEXT && (
            <Controller
              name="freeText"
              control={control}
              render={({ field }) => (
                <FreeText
                  isFullscreenMode={false}
                  onChange={field.onChange}
                  value={field.value}
                  explanation={currentQuestion.explanation}
                />
              )}
            />
          )}

          {questionType === QUESTION_TYPE.DRAGDROP && (
            <Controller
              name="dragDrop"
              control={control}
              render={({ field }) => (
                <DragAndDrop
                  onChange={field.onChange}
                  questions={currentQuestion.data?.map((item) => item.content)}
                  value={field.value}
                />
              )}
            />
          )}

          {questionType === QUESTION_TYPE.MTC && (
            <Controller
              name="multipleChoice"
              control={control}
              render={({ field }) => (
                <MultipleChoice
                  question={currentQuestion.data ?? []}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </Col>

        <Col span={24} className="d-flex align-items-center">
          {questionNumber !== 1 && (
            <ThemeButton
              padding="7px 18px"
              className="rounded-pill"
              variant="secondary"
              type="button"
              disabled={isSubmitting}
              onClick={goPrev}
            >
              Previous
            </ThemeButton>
          )}
          <div className="d-flex align-items-center ml-auto">
            {questionNumber < totalQuestions && (
              <ThemeButton
                padding="7px 18px"
                className="rounded-pill mr-1"
                variant="secondary"
                type="button"
                disabled={isSubmitting}
                onClick={goNext}
              >
                Next
              </ThemeButton>
            )}

            <ThemeButton
              type="button"
              padding="7px 18px"
              className="rounded-pill"
              disabled={isSubmitting}
              onClick={() =>
                handleSubmit((formData) => onSubmit(formData, 'submit'))()
              }
            >
              Submit
            </ThemeButton>
          </div>
        </Col>
      </Row>
    </Form>
  );
}

export default QuestionList;
